/* tslint:disable:max-line-length */
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { MmProjectLoaderService } from '../../mm-project/mm-project-loader.service';
import { MmViewService } from '../../mm-view.service';
import { LoadStatusEnum } from '@docma-mm-core/model/load-state/load-state.enum';
import { FieldMinimal } from '@docma-mm-core/field/field';
import { FIELD_DICT_MAPPING, FieldMappingEnum } from './field-mapping.enum';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@mydocma/common/src/lib/logger';

export interface FieldConfiguration {
  fieldId: string;
  displayName: string;
  isVisible: boolean;
}

@Injectable()
export class FieldsConfigurationService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private fieldConfigurationsSubject = new BehaviorSubject<Map<string, FieldConfiguration>>(new Map());

  public fieldConfigurations$ = this.fieldConfigurationsSubject.asObservable();

  constructor(
    private readonly mmViewService: MmViewService,
    private readonly projectLoaderService: MmProjectLoaderService,
    private readonly translateService: TranslateService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(async (project) => {
        if (project?.MyDocmaUid) {
          await this.loadFieldConfigurations();
        }
      });
  }

  private async loadFieldConfigurations(): Promise<void> {
    try {
      const fieldDict = Object.fromEntries(await firstValueFrom(this.projectLoaderService.fields
        .pipe(
          filter(f => f.loadState.status === LoadStatusEnum.Loaded),
          map(m => {
            return m.data.filter(x => x.Id != null).map(x => [x.Id.toString(), x]);
          }),
        )));

      const configurations = new Map<string, FieldConfiguration>();

      Object.values(FieldMappingEnum).forEach((filterFieldName) => {
        const mappedFieldId = FIELD_DICT_MAPPING[filterFieldName];
        if (mappedFieldId) {
          const field = Object.values(fieldDict).find((f: FieldMinimal) => f.Id === mappedFieldId);
          if (field) {
            let displayName = field.Project || field.Global || field.Id;
            const isVisible = !!displayName;

            if (field.Id === FIELD_DICT_MAPPING[FieldMappingEnum.Classification1Name1] || field.Id === FIELD_DICT_MAPPING[FieldMappingEnum.Classification2Name1] || field.Id === FIELD_DICT_MAPPING[FieldMappingEnum.Classification3Name1] || field.Id === FIELD_DICT_MAPPING[FieldMappingEnum.Classification4Name1]) {
              displayName = field.GroupFields[0].Project || field.GroupFields[0].Global || field.Id;
            }

            configurations.set(filterFieldName, {
              fieldId: mappedFieldId,
              displayName: displayName || '',
              isVisible
            });
          }
          // TODO: remove temporary fallback display names for testing
          // TEMPORARY: fallback display names for testing
          // else if (filterFieldName === FieldMappingEnum.AttributeName1) {
          //   const displayName = this.translateService.instant('MM.fields.filter.attributeName1');
          //   configurations.set(filterFieldName, {
          //     fieldId: mappedFieldId,
          //     displayName: displayName || 'Attribut Kurz',
          //     isVisible: true
          //   });
          // }
        }
      });

      this.fieldConfigurationsSubject.next(configurations);
    } catch (error) {
      this.logger.error(error, '', 'FieldsConfigurationService');
      this.fieldConfigurationsSubject.next(new Map());
    }
  }

  public getFieldConfiguration(filterFieldName: string): FieldConfiguration | undefined {
    const currentConfigurations = this.fieldConfigurationsSubject.getValue();
    return currentConfigurations.get(filterFieldName);
  }

  public isFieldVisible(filterFieldName: string): boolean {
    const config = this.getFieldConfiguration(filterFieldName);
    return config?.isVisible ?? false;
  }

  public getFieldDisplayName(filterFieldName: string): string {
    const config = this.getFieldConfiguration(filterFieldName);

    // TODO remove temporary fallback display names for testing
    // TEMPORARY: fallback display names for testing
    // if (filterFieldName === 'lastActivitySCDescription') {
    //   return config?.displayName || 'NU Vorgang';
    // }
    // if (filterFieldName === 'lastActivityCTDescription') {
    //   return config?.displayName || 'AG Vorgang';
    // }

    return config?.displayName || '';
  }

  public getVisibleFilterFields(): string[] {
    const currentConfigurations = this.fieldConfigurationsSubject.getValue();
    const visibleFields = Array.from(currentConfigurations.entries())
      .filter(([_, config]) => config.isVisible)
      .map(([filterFieldName, _]) => filterFieldName);

    console.log('[FieldsConfigurationService] original visible fields:', visibleFields);

    // TODO remove temporary force activity description fields to be visible for testing
    // TEMPORARY: force activity description fields to be visible for testing
    // if (!visibleFields.includes('lastActivitySCDescription')) {
    //   visibleFields.push('lastActivitySCDescription');
    // }
    // if (!visibleFields.includes('lastActivityCTDescription')) {
    //   visibleFields.push('lastActivityCTDescription');
    // }

    console.log('[FieldsConfigurationService] all field configurations:');
    const allConfigurations = this.fieldConfigurationsSubject.getValue();
    // allConfigurations.forEach((config, fieldName) => {
    //   console.log(`  ${fieldName}: visible=${config.isVisible}, displayName="${config.displayName}", fieldId=${config.fieldId}`);
    // });

    console.log('[FieldsConfigurationService] activity description fields status:');
    const scConfig = allConfigurations.get('lastActivitySCDescription');
    const ctConfig = allConfigurations.get('lastActivityCTDescription');
    console.log(`  SC Description: ${scConfig ? `visible=${scConfig.isVisible}, displayName="${scConfig.displayName}"` : 'NOT FOUND'}`);
    console.log(`  CT Description: ${ctConfig ? `visible=${ctConfig.isVisible}, displayName="${ctConfig.displayName}"` : 'NOT FOUND'}`);


    return visibleFields;
  }
}
